import { motion } from "framer-motion";
import { MapPin, Navigation } from "lucide-react";

const STRUCTURE = {
  name: "Parc des Sports de la Coudoulière",
  city: "Martigues (13)",
  capacity: "Notre terrain principal",
  description:
    "Complexe sportif idéal pour les séances d'entraînement et le travail technique. Terrains entretenus, accès facile et cadre dégagé pour s'exercer en conditions optimales — l'environnement parfait pour développer chaque gardien.",
  image:
    "https://customer-assets.emergentagent.com/job_football-training-37/artifacts/lnd4t5aq_parc%20des%20sports%20de%20la%20coudouille%CC%80re.%20.jpeg",
  address: "7 Rue des Clématites",
  postalCity: "13500 Martigues",
  mapsUrl:
    "https://www.google.com/maps/search/?api=1&query=7+Rue+des+Cle%CC%81matites+13500+Martigues",
};

export default function Structures() {
  return (
    <section
      id="structures"
      data-testid="structures-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
            >
              NOTRE
              <br />
              <span className="text-[#CCFF00]">STRUCTURE.</span>
            </motion.h2>
          </div>
          <p className="lg:col-span-5 text-zinc-400 text-base md:text-lg leading-relaxed">
            Une installation sportive professionnelle et reconnue, choisie pour
            offrir aux gardiens les meilleures conditions de travail possibles.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Photo + Description */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8 group bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden hover:border-[#CCFF00]/40 transition-colors"
            data-testid="structure-card"
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                <img
                  src={STRUCTURE.image}
                  alt={STRUCTURE.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-black/70 backdrop-blur-sm px-3 py-1.5 border border-white/10">
                  <MapPin className="w-3 h-3 text-[#CCFF00]" strokeWidth={2} />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white">
                    {STRUCTURE.city}
                  </span>
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  {STRUCTURE.capacity}
                </p>
                <h3 className="mt-3 font-display text-3xl md:text-4xl leading-tight group-hover:text-[#CCFF00] transition-colors">
                  {STRUCTURE.name}
                </h3>
                <p className="mt-4 text-sm md:text-base text-zinc-400 leading-relaxed">
                  {STRUCTURE.description}
                </p>
              </div>
            </div>
          </motion.article>

          {/* Address card with embedded map */}
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-4 bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden flex flex-col"
            data-testid="structure-address-card"
          >
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#CCFF00] mb-5">
                <Navigation className="w-3.5 h-3.5" /> Adresse du stade
              </div>
              <p className="font-display text-3xl md:text-4xl leading-[0.95]">
                {STRUCTURE.address}
              </p>
              <p className="mt-2 font-display text-2xl md:text-3xl leading-none text-[#CCFF00]">
                {STRUCTURE.postalCity}
              </p>
              <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
                Parking gratuit sur place. Accès facile depuis l&apos;autoroute A55
                et le centre-ville de Martigues.
              </p>
            </div>
            <div className="relative w-full aspect-[4/3] md:aspect-square border-t border-white/10">
              <iframe
                title="Carte du Parc des Sports de la Coudoulière"
                src="https://www.google.com/maps?q=7+Rue+des+Cl%C3%A9matites+13500+Martigues&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full grayscale-[35%] contrast-110"
                allowFullScreen
                data-testid="structure-map-iframe"
              />
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
